import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { api, type Book, type Page } from "../api";
import i18n from "../i18n";
import type { RenderLongImageResult } from "./longImage";

export function shareUrlFor(shortId: string): string {
  // Resolve against the current origin so the link is whatever host the
  // visitor is using (handy for prod / preview / localhost).
  if (typeof window === "undefined") return `/share/${shortId}`;
  return `${window.location.origin}/share/${shortId}`;
}

export function bookIsShareable(book: Pick<Book, "status" | "archivedAt">): boolean {
  return book.status === "ready" && !book.archivedAt;
}

type ToastStatus = "idle" | "copied" | "shared" | "error";

// ---------------------------------------------------------------------------
// B3 — mobile share-disclosure gate
// ---------------------------------------------------------------------------

const SHARE_ACK_KEY = "tp.sharePublicAck";

/**
 * Pure helper — returns true when the mobile-share confirm must be shown
 * (i.e. the acknowledgment flag has NOT been set yet).
 *
 * Accepts the stored value so it can be tested without touching globals.
 */
export function shouldGateMobileShare(ackFlagValue: string | null): boolean {
  return ackFlagValue === null;
}

function readShareAck(): string | null {
  if (typeof window === "undefined") return null;
  try {
    return window.localStorage.getItem(SHARE_ACK_KEY);
  } catch {
    return null;
  }
}

function writeShareAck(): void {
  if (typeof window === "undefined") return;
  try {
    window.localStorage.setItem(SHARE_ACK_KEY, "1");
  } catch {
    // storage unavailable — fall through; the gate is best-effort
  }
}

/**
 * The actual mobile share chain: navigator.share → clipboard fallback → toast.
 * Extracted so both `share()` (when ack is already set) and the confirm-sheet
 * button (after setting the ack) can invoke it without duplication.
 */
async function runMobileShareChain({
  url,
  title,
  flash,
  setStatus,
  setErrorMessage,
}: {
  url: string;
  title?: string;
  flash: (s: ToastStatus) => void;
  setStatus: (s: ToastStatus) => void;
  setErrorMessage: (m: string | undefined) => void;
}): Promise<void> {
  if (typeof navigator !== "undefined" && typeof navigator.share === "function") {
    try {
      await navigator.share({ title, url });
      flash("shared");
      return;
    } catch (err) {
      // AbortError = user cancelled the share sheet — treat as a non-event.
      if (err instanceof DOMException && err.name === "AbortError") {
        setStatus("idle");
        return;
      }
      // Otherwise fall through to clipboard.
    }
  }
  try {
    await navigator.clipboard.writeText(url);
    flash("copied");
  } catch {
    setErrorMessage(undefined);
    flash("error");
  }
}

/** Same desktop breakpoint the Reader uses for two-up vs single-page. */
function isDesktopViewport(): boolean {
  if (typeof window === "undefined") return true;
  return window.matchMedia("(min-width: 760px)").matches;
}

export type ImageState =
  | { kind: "idle" }
  | { kind: "preparing" }
  | { kind: "ready"; result: RenderLongImageResult; title?: string }
  | { kind: "ready-fallback"; result: RenderLongImageResult; title?: string }
  | { kind: "error" };

export function isMobileViewport(): boolean {
  return !isDesktopViewport();
}

export function sanitizeFilename(input: string): string {
  const cleaned = input
    .toLowerCase()
    .replace(/\s+/g, "-")
    .replace(/[^a-z0-9-_]/g, "-")
    .replace(/-+/g, "-")
    .slice(0, 64)
    .replace(/^-+|-+$/g, "");
  return cleaned || "teenypages-book";
}

/**
 * Share affordance for any "ready" book. On desktop we open a modal that
 * shows the link with a copy button — clipboard-only flows on desktop are
 * easy to miss (the link silently lands on the clipboard with no
 * confirmation context). On mobile we hand off to the OS share sheet when
 * the browser supports it; otherwise we copy and surface a toast.
 *
 * `dialog` is the currently-open share modal (rendered by the caller via
 * `<ShareDialog/>`); `status` drives the optional toast.
 */
export function useShare(): {
  status: ToastStatus;
  dialog: { url: string; title?: string } | null;
  /** On mobile, when the ack flag is unset this is set instead of invoking share directly. */
  pendingShare: { url: string; title?: string } | null;
  clearPendingShare: () => void;
  /**
   * Run the mobile share chain with the hook's flash state. Call this
   * synchronously from a click handler (e.g. the confirm-sheet confirm button)
   * so the user gesture is intact for navigator.share.
   */
  runMobileShare: (args: { url: string; title?: string }) => Promise<void>;
  share: (args: { url: string; title?: string }) => Promise<void>;
  closeDialog: () => void;
  openDialog: (args: { url: string; title?: string }) => void;
  shareLinkDirect: (args: { url: string; title?: string }) => Promise<void>;
  errorMessage: string | undefined;
  imageState: ImageState;
  prepareImage: (args: { shortId: string; title?: string }) => Promise<void>;
  deliverImage: () => Promise<void>;
  openImageInNewTab: () => void;
  resetImage: () => void;
} {
  const { t } = useTranslation();
  const [status, setStatus] = useState<ToastStatus>("idle");
  const [dialog, setDialog] = useState<{ url: string; title?: string } | null>(null);
  const [pendingShare, setPendingShare] = useState<{ url: string; title?: string } | null>(null);
  const [errorMessage, setErrorMessage] = useState<string | undefined>(undefined);
  const [imageState, setImageState] = useState<ImageState>({ kind: "idle" });
  const generationIdRef = useRef(0);
  const timer = useRef<number | null>(null);

  useEffect(() => {
    return () => {
      if (timer.current) window.clearTimeout(timer.current);
    };
  }, []);

  const flash = (s: ToastStatus) => {
    setStatus(s);
    if (timer.current) window.clearTimeout(timer.current);
    timer.current = window.setTimeout(() => setStatus("idle"), 1800);
  };

  const share = async ({ url, title }: { url: string; title?: string }) => {
    // Desktop: surface the link in a modal. `navigator.share` exists on
    // some desktop browsers (Safari macOS) but the system share sheet is a
    // worse fit for "copy a link to send a friend" than a visible URL.
    if (isDesktopViewport()) {
      setDialog({ url, title });
      return;
    }
    // Mobile: gate the whole action (navigator.share + clipboard fallback)
    // behind a one-time public-disclosure acknowledgment. When the flag is
    // unset, surface a confirm sheet instead; the confirm button's click
    // handler runs the chain directly (preserving the user gesture that
    // navigator.share requires). When already acknowledged, run directly.
    if (shouldGateMobileShare(readShareAck())) {
      setPendingShare({ url, title });
      return;
    }
    await runMobileShareChain({ url, title, flash, setStatus, setErrorMessage });
  };

  /**
   * Forcibly open the share dialog regardless of viewport. Reader uses
   * this from its topbar so mobile users see the new "Save as image"
   * action; share() is left alone so Home/Archive keep their direct
   * mobile share-sheet path.
   */
  const openDialog = ({ url, title }: { url: string; title?: string }) => {
    setDialog({ url, title });
  };

  /**
   * Mobile-style share chain — navigator.share -> clipboard -> toast —
   * runs regardless of viewport. The in-dialog "Share link" row uses
   * this instead of share() because share() on desktop opens the
   * dialog, which would recurse.
   */
  const shareLinkDirect = async ({ url, title }: { url: string; title?: string }) => {
    setErrorMessage(undefined);
    if (typeof navigator !== "undefined" && typeof navigator.share === "function") {
      try {
        await navigator.share({ title, url });
        flash("shared");
        return;
      } catch (err) {
        if (err instanceof DOMException && err.name === "AbortError") {
          setStatus("idle");
          return;
        }
        // fall through to clipboard
      }
    }
    try {
      await navigator.clipboard.writeText(url);
      flash("copied");
    } catch {
      setErrorMessage(undefined);
      flash("error");
    }
  };

  const resetImage = () => {
    generationIdRef.current += 1;
    setImageState((prev) => {
      if (prev.kind === "ready" || prev.kind === "ready-fallback") {
        try {
          URL.revokeObjectURL(prev.result.objectUrl);
        } catch {
          // best-effort; revoking an already-revoked URL is a no-op
        }
      }
      return { kind: "idle" };
    });
  };

  const prepareImage = async ({
    shortId,
    title,
  }: {
    shortId: string;
    title?: string;
  }) => {
    const id = ++generationIdRef.current;
    setErrorMessage(undefined);
    setImageState({ kind: "preparing" });

    let fresh: { ok: true; book: Book; pages: Page[] } | null = null;
    try {
      const result = await api.getSharedBook(shortId);
      if (id !== generationIdRef.current) return;
      if (!result.ok) {
        setErrorMessage(t("share.errLoad"));
        setImageState({ kind: "error" });
        flash("error");
        return;
      }
      fresh = result;
    } catch {
      if (id !== generationIdRef.current) return;
      setErrorMessage(t("share.errLoad"));
      setImageState({ kind: "error" });
      flash("error");
      return;
    }

    let renderModule: typeof import("./longImage");
    try {
      renderModule = await import("./longImage");
    } catch {
      if (id !== generationIdRef.current) return;
      setErrorMessage(t("share.errImage"));
      setImageState({ kind: "error" });
      flash("error");
      return;
    }
    if (id !== generationIdRef.current) return;

    try {
      const rendered = await renderModule.renderLongImage({
        book: fresh.book,
        pages: fresh.pages,
        shareUrl: shareUrlFor(fresh.book.shortId),
      });
      if (id !== generationIdRef.current) {
        try {
          URL.revokeObjectURL(rendered.objectUrl);
        } catch {
          // no-op
        }
        return;
      }
      setImageState({ kind: "ready", result: rendered, title });
    } catch {
      if (id !== generationIdRef.current) return;
      setErrorMessage(t("share.errImage"));
      setImageState({ kind: "error" });
      flash("error");
    }
  };

  const doDelivery = async (
    state: Extract<ImageState, { kind: "ready" } | { kind: "ready-fallback" }>,
  ) => {
    const { result, title } = state;
    const filename = sanitizeFilename(title ?? "teenypages-book") + ".png";
    const pngFile = new File([result.blob], filename, { type: "image/png" });

    if (state.kind === "ready-fallback") {
      window.open(result.objectUrl, "_blank", "noopener");
      resetImage();
      return;
    }

    if (isMobileViewport()) {
      const canShareFiles =
        typeof navigator !== "undefined" &&
        typeof navigator.canShare === "function" &&
        navigator.canShare({ files: [pngFile] });

      if (canShareFiles && typeof navigator.share === "function") {
        try {
          await navigator.share({ files: [pngFile], title });
          flash("shared");
          resetImage();
          return;
        } catch (err) {
          if (err instanceof DOMException && err.name === "AbortError") {
            // Leave the dialog in `ready` so the user can try again.
            return;
          }
          // Non-Abort: don't try window.open from here — transient
          // activation is likely gone. Swap to ready-fallback so the
          // user's next tap opens a new tab synchronously.
          setErrorMessage(t("share.errShareSheet"));
          setImageState({ kind: "ready-fallback", result, title });
          flash("error");
          return;
        }
      }

      // Web Share unsupported at all: open in new tab now.
      window.open(result.objectUrl, "_blank", "noopener");
      resetImage();
      return;
    }

    // Desktop: always download, never call navigator.share.
    const a = document.createElement("a");
    a.href = result.objectUrl;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    a.remove();
    resetImage();
  };

  const deliverImage = async () => {
    // We need the latest imageState for the deliver dispatch. Use the
    // setter's functional form to read it synchronously, then schedule
    // the async work outside the setter callback.
    let snapshot: ImageState | null = null;
    setImageState((prev) => {
      snapshot = prev;
      return prev;
    });
    if (!snapshot) return;
    const state = snapshot as ImageState;
    if (state.kind !== "ready" && state.kind !== "ready-fallback") return;
    await doDelivery(state);
  };

  /**
   * Open the rendered image in a new tab. Uses an anchor click rather
   * than window.open because Safari (and sometimes Chrome) block
   * window.open against blob: URLs even from a user gesture — the
   * anchor-click path is allowed for the same reason download links
   * work. Leaves imageState in `ready` so the user can still use the
   * primary delivery action afterward.
   */
  const openImageInNewTab = () => {
    let snapshot: ImageState | null = null;
    setImageState((prev) => {
      snapshot = prev;
      return prev;
    });
    if (!snapshot) return;
    const state = snapshot as ImageState;
    if (state.kind !== "ready" && state.kind !== "ready-fallback") return;
    const a = document.createElement("a");
    a.href = state.result.objectUrl;
    a.target = "_blank";
    a.rel = "noopener";
    document.body.appendChild(a);
    a.click();
    a.remove();
  };

  const runMobileShare = async ({ url, title }: { url: string; title?: string }) => {
    await runMobileShareChain({ url, title, flash, setStatus, setErrorMessage });
  };

  return {
    status,
    dialog,
    pendingShare,
    clearPendingShare: () => setPendingShare(null),
    runMobileShare,
    share,
    closeDialog: () => setDialog(null),
    openDialog,
    shareLinkDirect,
    errorMessage,
    imageState,
    prepareImage,
    deliverImage,
    openImageInNewTab,
    resetImage,
  };
}

function deliveryLabel(
  state: Extract<ImageState, { kind: "ready" } | { kind: "ready-fallback" }>,
): string {
  if (state.kind === "ready-fallback") return i18n.t("share.deliveryOpen");
  if (isMobileViewport()) {
    try {
      const probe = new File([new Blob()], "probe.png", { type: "image/png" });
      if (
        typeof navigator !== "undefined" &&
        typeof navigator.canShare === "function" &&
        navigator.canShare({ files: [probe] })
      ) {
        return i18n.t("share.deliveryShare");
      }
    } catch {
      // canShare can throw on some browsers — fall through
    }
    return i18n.t("share.deliveryOpen");
  }
  return i18n.t("share.deliveryDownload");
}

/**
 * Desktop share modal — shows the link, a copy button, and a short
 * instruction. Selecting the input on focus saves a step for users who
 * prefer Cmd-C. Closes on overlay click and Escape.
 */
export function ShareDialog({
  open,
  url,
  title,
  onClose,
  imageEnabled,
  imageState,
  prepareImage,
  deliverImage,
  openImageInNewTab,
  resetImage,
  shortId,
  shareLinkDirect,
}: {
  open: boolean;
  url: string;
  title?: string;
  onClose: () => void;
  imageEnabled?: boolean;
  imageState?: ImageState;
  prepareImage?: (args: { shortId: string; title?: string }) => Promise<void>;
  deliverImage?: () => Promise<void>;
  openImageInNewTab?: () => void;
  resetImage?: () => void;
  shortId?: string;
  shareLinkDirect?: (args: { url: string; title?: string }) => Promise<void>;
}) {
  const { t } = useTranslation();
  const inputRef = useRef<HTMLInputElement | null>(null);
  const [copied, setCopied] = useState(false);
  const copiedTimer = useRef<number | null>(null);

  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, onClose]);

  useEffect(() => {
    if (!open) {
      setCopied(false);
      if (copiedTimer.current) window.clearTimeout(copiedTimer.current);
      return;
    }
    // On mobile, focusing the input pops the soft keyboard and covers
    // the bottom sheet's action buttons. The input is still readable
    // and tappable for manual selection.
    if (!isDesktopViewport()) return;
    const id = window.setTimeout(() => {
      inputRef.current?.focus();
      inputRef.current?.select();
    }, 30);
    return () => window.clearTimeout(id);
  }, [open]);

  useEffect(() => {
    return () => {
      if (copiedTimer.current) window.clearTimeout(copiedTimer.current);
    };
  }, []);

  if (!open) return null;

  const onCopy = async () => {
    try {
      await navigator.clipboard.writeText(url);
      setCopied(true);
    } catch {
      // Clipboard refused (e.g. permissions / insecure context). Re-select
      // the input so the user can copy manually with the keyboard.
      inputRef.current?.focus();
      inputRef.current?.select();
      setCopied(false);
      return;
    }
    if (copiedTimer.current) window.clearTimeout(copiedTimer.current);
    copiedTimer.current = window.setTimeout(() => setCopied(false), 1800);
  };

  return (
    <div
      className="tp-sheet-overlay"
      onClick={onClose}
      role="dialog"
      aria-modal="true"
      aria-labelledby="tp-share-title"
    >
      <div
        className="tp-sheet"
        onClick={(e) => e.stopPropagation()}
        style={{ maxWidth: 520 }}
      >
        <div style={{ display: "flex", justifyContent: "center", marginBottom: 14 }}>
          <div style={{
            width: 40, height: 4, borderRadius: 999,
            background: "var(--tp-ink-soft)", opacity: 0.3,
          }} />
        </div>
        <div id="tp-share-title" className="tp-h1" style={{ marginBottom: 6 }}>
          {t("share.dialogTitle", { book: title ? `"${title}"` : t("share.thisBook") })}
        </div>
        <div
          className="tp-body"
          style={{ color: "var(--tp-ink-soft)", marginBottom: 16, fontSize: 15 }}
        >
          {t("share.dialogBody")}
        </div>
        <div
          className="tp-body"
          style={{ color: "var(--tp-ink-soft)", marginBottom: 16, fontSize: 15 }}
        >
          {t("share.publicNotice")}
        </div>

        <div style={{ display: "flex", gap: 8, marginBottom: 16 }}>
          <input
            ref={inputRef}
            className="tp-input"
            readOnly
            value={url}
            onFocus={(e) => e.currentTarget.select()}
            onClick={(e) => e.currentTarget.select()}
            style={{ flex: 1, minWidth: 0, fontSize: 15 }}
            aria-label={t("share.shareableLink")}
          />
          {isMobileViewport() && shareLinkDirect ? (
            <button
              type="button"
              className="tp-btn tp-btn-primary"
              onClick={() => void shareLinkDirect({ url, title })}
              style={{ padding: "0 18px", fontSize: 15, whiteSpace: "nowrap" }}
            >
              {t("share.shareLink")}
            </button>
          ) : (
            <button
              type="button"
              className="tp-btn tp-btn-primary"
              onClick={() => void onCopy()}
              style={{ padding: "0 18px", fontSize: 15, whiteSpace: "nowrap" }}
            >
              {copied ? t("share.copied") : t("share.copy")}
            </button>
          )}
        </div>

        {imageEnabled && imageState && (
          <div style={{ marginBottom: 16 }}>
            {(imageState.kind === "idle" || imageState.kind === "error") ? (
              <button
                type="button"
                className="tp-btn tp-btn-block"
                onClick={() => {
                  if (!prepareImage || !shortId) return;
                  void prepareImage({ shortId, title });
                }}
                aria-busy={false}
              >
                {imageState.kind === "error" ? t("common.retry") : t("share.saveAsImage")}
              </button>
            ) : null}

            {imageState.kind === "preparing" ? (
              <button
                type="button"
                className="tp-btn tp-btn-block"
                disabled
                aria-busy={true}
              >
                {t("share.makingImage")}
              </button>
            ) : null}

            {(imageState.kind === "ready" || imageState.kind === "ready-fallback") ? (
              <div>
                <div
                  style={{
                    display: "flex",
                    justifyContent: "center",
                    marginBottom: 12,
                  }}
                >
                  <img
                    src={imageState.result.objectUrl}
                    alt=""
                    style={{
                      maxHeight: 220,
                      maxWidth: "100%",
                      objectFit: "contain",
                      border: "1px solid var(--tp-ink-soft)",
                      borderRadius: 6,
                    }}
                  />
                </div>
                <button
                  type="button"
                  className="tp-btn tp-btn-primary tp-btn-block"
                  onClick={() => void deliverImage?.()}
                >
                  {deliveryLabel(imageState)}
                </button>
                <button
                  type="button"
                  className="tp-btn tp-btn-block"
                  onClick={() => openImageInNewTab?.()}
                  style={{ marginTop: 8 }}
                >
                  {t("share.openNewTab")}
                </button>
              </div>
            ) : null}
          </div>
        )}

        <div
          className="tp-body"
          style={{ color: "var(--tp-ink-soft)", fontSize: 13, marginBottom: 18 }}
        >
          {t("share.takeOffline")}
        </div>

        <button
          type="button"
          className="tp-btn tp-btn-block"
          onClick={onClose}
          autoFocus={isDesktopViewport()}
        >
          {t("common.done")}
        </button>
      </div>
    </div>
  );
}

/**
 * B3 — Mobile public-disclosure confirm sheet.
 *
 * Shown once (before `tp.sharePublicAck` is set) when the user taps Share on
 * mobile from a view that calls `share()` (Home). On confirm it sets the
 * localStorage ack flag then calls `onConfirm` synchronously — the caller
 * must run the mobile share chain from that synchronous call so the user
 * gesture is preserved for navigator.share. On cancel calls `onCancel`.
 */
export function ShareConfirmSheet({
  pending,
  onConfirm,
  onCancel,
}: {
  pending: { url: string; title?: string } | null;
  /** Called synchronously from the confirm button's click — run navigator.share here. */
  onConfirm: (args: { url: string; title?: string }) => void;
  onCancel: () => void;
}) {
  const { t } = useTranslation();

  if (!pending) return null;

  const { url, title } = pending;

  const handleConfirm = () => {
    writeShareAck();
    onConfirm({ url, title });
  };

  return (
    <div
      className="tp-sheet-overlay"
      onClick={onCancel}
      role="dialog"
      aria-modal="true"
      aria-labelledby="tp-share-confirm-title"
    >
      <div
        className="tp-sheet"
        onClick={(e) => e.stopPropagation()}
        style={{ maxWidth: 520 }}
      >
        <div style={{ display: "flex", justifyContent: "center", marginBottom: 14 }}>
          <div style={{
            width: 40, height: 4, borderRadius: 999,
            background: "var(--tp-ink-soft)", opacity: 0.3,
          }} />
        </div>
        <div id="tp-share-confirm-title" className="tp-h1" style={{ marginBottom: 6 }}>
          {t("share.dialogTitle", { book: title ? `"${title}"` : t("share.thisBook") })}
        </div>
        <div
          className="tp-body"
          style={{ color: "var(--tp-ink-soft)", marginBottom: 20, fontSize: 15 }}
        >
          {t("share.publicNotice")}
        </div>
        <button
          type="button"
          className="tp-btn tp-btn-primary tp-btn-block"
          onClick={handleConfirm}
        >
          {t("common.share")}
        </button>
        <button
          type="button"
          className="tp-btn tp-btn-block"
          onClick={onCancel}
          style={{ marginTop: 8 }}
        >
          {t("common.cancel")}
        </button>
      </div>
    </div>
  );
}
